import React, { useState } from "react";
import { Topbar, SidebarNav } from "components/layout";
import { useAuth } from "contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import PaystackPop from '@paystack/inline-js';
import { userAPI } from "services/api";

const quickAmounts = [20000, 50000, 100000, 200000];

const FundingPage: React.FC = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        amount: "",
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [fundingCompleted, setFundingCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { user } = useAuth();
    const balance = parseFloat(user?.wallet_balance || "0.00");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleQuickAmount = (amt: number) => {
        setForm({ ...form, amount: amt.toString() });
        setErrors({ ...errors, amount: "" });
    };

    const validateForm = () => {
        let newErrors: { [key: string]: string } = {};
        if (!form.amount) {
            newErrors.amount = "Enter an amount";
        } else if (parseFloat(form.amount) <= 0) {
            newErrors.amount = "Enter a valid amount greater than zero";
        } else if (parseFloat(form.amount) < 100) {
            newErrors.amount = "Minimum funding amount is ₦100";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCancel = () => {
        navigate('/');
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const response = await userAPI.fundWallet(form.amount);
            
            const popup = new PaystackPop();
            popup.resumeTransaction(response.data.access_code, {
                onSuccess: () => {
                    // setFundingCompleted(true);
                },
                onCancel: () => {
                    console.log('Payment cancelled');
                }
            });
        } catch (err: any) {
            console.error('Error initiating payment:', err);
            setErrors({ ...errors, api: 'An error occurred while processing payment.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Success page
    if (fundingCompleted) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Topbar />
                <div className="flex">
                    <SidebarNav />
                    <main className="flex-1 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-md w-full max-w-md p-10 flex flex-col items-center">
                            <span className="text-3xl mb-4">💳</span>
                            <h2 className="text-center text-xl font-semibold mb-2">Wallet funded successfully!</h2>
                            <p className="text-center text-gray-600 mb-6">
                                Your wallet has been credited with <span className="font-medium">₦{parseFloat(form.amount).toLocaleString()}</span>.
                                <br />The funds are now available for use.
                            </p>
                            <button
                                className="bg-primary-700 text-white rounded-lg px-6 py-2 font-medium hover:bg-primary-800 transition"
                                onClick={() => setFundingCompleted(false)}
                            >
                                Fund wallet again
                            </button>
                        </div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Topbar />
            <div className="flex">
                <SidebarNav />
                <main className="flex-1 flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-8 py-6">
                        <div className="flex items-center space-x-2">
                            <button
                                className="text-sm text-gray-500"
                                onClick={() => navigate('/')}
                            >
                                &larr; Back
                            </button>
                        </div>
                        <button className="text-red-500 text-sm font-medium" onClick={handleCancel}>
                            Cancel <span className="text-xl font-light ml-1">&times;</span>
                        </button>
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-1 items-center justify-center px-4">
                        <div className="bg-white rounded-2xl shadow-sm w-full max-w-xl px-6 py-10 sm:px-10 sm:py-12 lg:px-16 lg:py-16">
                            <h2 className="text-center text-2xl font-semibold mb-1">Fund Wallet</h2>
                            <p className="text-center text-gray-500 text-sm mb-6">Add money to your wallet</p>

                            <form className="flex flex-col space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-1">How much do you want to add?</label>
                                    <div className="relative flex items-center mt-2">
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl font-medium pl-1">
                                            ₦
                                        </span>
                                        <input
                                            type="number"
                                            name="amount"
                                            placeholder="0.00"
                                            value={form.amount}
                                            onChange={handleChange}
                                            className="w-full pl-8 pr-4 bg-transparent border-0 border-b-2 border-gray-200 focus:border-primary-400 rounded-none py-3 text-3xl font-medium focus:outline-none transition placeholder-gray-300"
                                            style={{ letterSpacing: "1px" }}
                                        />
                                    </div>
                                    <div className="mt-2 text-xs text-gray-400">
                                        Current balance: ₦{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                    {errors.amount && <span className="block text-xs text-red-500 mt-1">{errors.amount}</span>}
                                    {errors.api && <span className="block text-xs text-red-500 mt-1">{errors.api}</span>}
                                </div>

                                {/* Quick Amount Buttons */}
                                <div className="flex flex-wrap gap-2 items-center">
                                    {quickAmounts.map((amt) => (
                                        <button
                                            type="button"
                                            key={amt}
                                            className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition focus:outline-primary-200 shadow-sm"
                                            onClick={() => handleQuickAmount(amt)}
                                        >
                                            ₦{amt.toLocaleString()}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex justify-end pt-4">
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className="w-[40%] bg-primary-700 text-white rounded-lg py-2 hover:bg-primary-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? "Processing..." : "Fund Wallet"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default FundingPage;